import type { Nullable } from '@zenos/shared-types';

/**
 * Returns a string representation of the date in the format 'dd/mm/yyyy'.
 * If the date is null, returns the `nullableSymbol` string.
 *
 * @example
 * ```js
 * getNumericDateString(new Date('2021-01-01')) // '01/01/2021'
 * getNumericDateString(null) // '-'
 * ```
 */
export function getNumericDateString(
  date: Nullable<Date>,
  nullableSymbol = '-',
) {
  if (!date) {
    return nullableSymbol;
  }
  return date.toLocaleString('en-GB', {
    day: 'numeric',
    month: 'numeric',
    year: 'numeric',
  });
}

/**
 * Returns a string representation of the date range in the format 'dd/mm/yyyy - dd/mm/yyyy'.
 * If the end date is not provided, returns the start date only.
 *
 * @example
 * ```js
 * getNumericDateRangeString({
 *  startDate: new Date('2021-01-01'),
 *  endDate: new Date('2021-01-31')
 * }) // '01/01/2021 - 31/01/2021'
 * getNumericDateRangeString({ startDate: new Date('2021-01-01') }) // '01/01/2021'
 * ```
 */
export function getNumericDateRangeString({
  startDate,
  endDate,
  separator = ' - ',
}: {
  startDate: Date;
  endDate?: Date;
  separator?: string;
}) {
  return endDate
    ? `${getNumericDateString(startDate)}${separator}${getNumericDateString(endDate)}`
    : getNumericDateString(startDate);
}

/**
 * Returns a string representation of the date in the format 'MMM yyyy'.
 *
 * @example
 * ```js
 * getShortMonthYearString(new Date('2021-01-01')) // 'Jan 2021'
 * ```
 */
export function getShortMonthYearString(date: Date) {
  return date.toLocaleDateString('en-GB', {
    year: 'numeric',
    month: 'short',
  });
}

/**
 * Returns a string representation of the date in the format 'mm dd'.
 *
 * @example
 * ```js
 * getShortMonthYearString(new Date('2021-01-01')) // 'Mon 08'
 * ```
 */
export function getShortWeekdayString(date: Date) {
  return date.toLocaleDateString('en-GB', {
    weekday: 'short',
    day: 'numeric',
  });
}

export function getDescriptiveWeekdayString(date: Date) {
  return date.toLocaleDateString('en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  });
}

/**
 * Returns a string representation of the date in the format 'MMMM yyyy'.
 *
 * @example
 * ```js
 * getLongMonthYearString(new Date('2021-01-01')) // 'January 2021'
 * ```
 */
export function getLongMonthYearString(date: Date) {
  return date.toLocaleDateString('en-GB', {
    year: 'numeric',
    month: 'long',
  });
}

/**
 * Returns a string representation of the date in the format 'MM yyyy'.
 *
 * @example
 * ```js
 * getLongMonthYearString(new Date('2021-01-01')) // '01 2021'
 * ```
 */
export function getShorterDateString(date: Date) {
  return new Date(date.getTime() - date.getTimezoneOffset() * 60000)
    .toISOString()
    .split('T')[0];
}
